// calibration.js - Director-only "calibrate evaluations" feature.

import { AUTH } from './auth.js';
import { CONFIG } from './config.js';
import { DB } from './db.js';
import { getDomains, getSkills } from './evaluation-data.js';
import { UI } from './ui.js';
import { UTILS } from './utils.js';

export const CALIBRATION = (() => {

  // ── STATE ──────────────────────────────────────────────────────────────────

  let _currentTeam   = null;
  let _allCeilings   = [];   // every row for the active season, used by the list and the per-team screen
  let _teams         = [];
  let _membershipCounts = {}; // team_id -> player count, for the team picker
  let _expanded      = new Set(); // pillar names whose per-skill overrides are visible
  let _lineFilter    = 'all';   // 'all' | 'girls' | 'boys'

  // ── EFFECTIVE CEILING LOOKUP ───────────────────────────────────────────────
  //
  // Public helper used by the rating screen. Given a team + domain (+ optional
  // skill), returns the max level allowed (1-5). Defaults to 5 if no ceiling
  // is set. Skill-specific row wins over pillar row.

  function getEffectiveCeiling(rows, domain, skill) {
    if (!rows?.length) return 5;
    const skillRow  = rows.find(r => r.domain === domain && r.skill === skill);
    if (skillRow) return skillRow.max_level;
    const pillarRow = rows.find(r => r.domain === domain && !r.skill);
    return pillarRow ? pillarRow.max_level : 5;
  }

  // Convenience wrapper for callers that don't already have the rows array.
  const _rowsCache = new Map(); // teamId -> rows[]
  async function fetchCeilingsForTeam(teamId, seasonId) {
    const key = `${teamId}__${seasonId}`;
    if (_rowsCache.has(key)) return _rowsCache.get(key);
    let rows = [];
    try { rows = await DB.ceilings.listForTeam(teamId, seasonId) || []; }
    catch { rows = []; }
    _rowsCache.set(key, rows);
    return rows;
  }
  function invalidateCache(teamId, seasonId) {
    _rowsCache.delete(`${teamId}__${seasonId}`);
  }

  // ── LIST SCREEN ────────────────────────────────────────────────────────────

  async function showList() {
    UI.showScreen('screen-calibrate-list');
    UI.setLoading('calibrate-list-content', 'Loading teams...');
    try {
      const season = CONFIG.season.current;
      const [teams, members, ceilings] = await Promise.all([
        DB.teams.getAll(),
        DB.memberships.getAllForSeason(season).catch(() => []),
        DB.ceilings.listForSeason(season).catch(() => []),
      ]);
      _teams       = teams || [];
      _allCeilings = ceilings || [];
      _membershipCounts = {};
      (members || []).forEach(m => {
        _membershipCounts[m.team_id] = (_membershipCounts[m.team_id] || 0) + 1;
      });
      _renderList();
    } catch (err) {
      console.error('Calibrate list load failed:', err);
      UI.setEmpty('calibrate-list-content', 'Could not load teams.');
    }
  }

  function _renderList() {
    const root = document.getElementById('calibrate-list-content');
    if (!root) return;
    const esc = UTILS.escapeHtml;

    if (!_teams.length) {
      UI.setEmpty('calibrate-list-content', 'No teams yet. Import a squad first.');
      return;
    }

    // Hook up the line filter pill.
    _lineFilter = UI.setupLineFilter('calibrate-line-filter', _teams, _lineFilter, v => {
      _lineFilter = v;
      _renderList();
    });

    // Group teams by their team-level ceiling state.
    const byTeam = {};
    _allCeilings.forEach(c => { (byTeam[c.team_id] = byTeam[c.team_id] || []).push(c); });

    const visibleTeams = _lineFilter === 'all'
      ? _teams
      : _teams.filter(t => (t.line || 'girls') === _lineFilter);

    const orderedTeams = visibleTeams.slice().sort((a, b) =>
      (a.age_group || '').localeCompare(b.age_group || '') ||
      (a.id || '').localeCompare(b.id || '')
    );

    let html = '<div class="calibrate-hint">Tap a team to set its score ceilings. New rules apply to coach ratings going forward - past ratings are kept and the dashboard will surface ones that exceed the new ceilings for review.</div>';

    let currentAge = null;
    orderedTeams.forEach(team => {
      if (team.age_group !== currentAge) {
        currentAge = team.age_group;
        html += `<div class="dash-section-title">${esc(currentAge)}</div>`;
      }
      const rows  = byTeam[team.id] || [];
      const count = _membershipCounts[team.id] || 0;

      // Build per-pillar summary chip: T3 Tc4 P5 M3 S4 (only show non-defaults).
      const summary = _pillarSummary(rows);

      const lineTag = (team.line || 'girls') === 'boys'
        ? '<span class="team-line-tag team-line-tag--boys">Boys</span>'
        : '<span class="team-line-tag team-line-tag--girls">Girls</span>';
      html += `
        <div class="calibrate-team-card" data-team-id="${esc(team.id)}">
          <div class="calibrate-team-card__row">
            <div>
              <div class="calibrate-team-card__name">${esc(team.id)}${lineTag}</div>
              <div class="calibrate-team-card__meta">${count} player${count === 1 ? '' : 's'}</div>
            </div>
            <div class="calibrate-team-card__summary">${summary}</div>
            <div class="calibrate-team-card__chevron">›</div>
          </div>
        </div>
      `;
    });

    root.innerHTML = html;
    root.querySelectorAll('[data-team-id]').forEach(c => {
      c.addEventListener('click', () => showTeam(c.dataset.teamId));
    });
  }

  function _pillarSummary(rows) {
    const esc = UTILS.escapeHtml;
    const ABBR = { Technical: 'T', Tactical: 'Tc', Physical: 'P', Mental: 'M', Social: 'S' };
    const domains = getDomains();
    const chips = domains
      .map(d => {
        const v = getEffectiveCeiling(rows.filter(r => !r.skill), d, null);
        const hasOverride = rows.some(r => r.domain === d && r.skill);
        if (v === 5 && !hasOverride) return null;
        return `<span class="calibrate-chip">${esc(ABBR[d] || d)}${v}${hasOverride ? '*' : ''}</span>`;
      })
      .filter(Boolean);
    if (!chips.length) return '<span class="calibrate-chip calibrate-chip--muted">Not set</span>';
    return chips.join('');
  }

  // ── TEAM SCREEN ────────────────────────────────────────────────────────────

  async function showTeam(teamId) {
    const team = _teams.find(t => t.id === teamId)
              || { id: teamId, age_group: '' };
    _currentTeam = team;
    _expanded = new Set();

    UI.showScreen('screen-calibrate-team');
    document.getElementById('calibrate-team-title').textContent = `Calibrate · ${team.id}`;
    document.getElementById('calibrate-team-sub').textContent =
      `${team.age_group}${CONFIG.season.label ? ' · ' + CONFIG.season.label : ''}`;

    UI.setLoading('calibrate-team-content', 'Loading current ceilings...');
    try {
      const season = CONFIG.season.current;
      const rows = await DB.ceilings.listForTeam(team.id, season);
      _rowsCache.set(`${team.id}__${season}`, rows || []);
      _renderTeam(rows || []);
      _maybeShowCopyButton(team.id);
    } catch (err) {
      console.error('Load ceilings failed:', err);
      UI.setEmpty('calibrate-team-content', 'Could not load ceilings.');
    }
  }

  async function _maybeShowCopyButton(teamId) {
    const btn = document.getElementById('btn-copy-prev-season');
    if (!btn) return;
    btn.style.display = 'none';
    try {
      const all = await DB.seasons.getAll();
      const idx = all.findIndex(s => s.id === CONFIG.season.current);
      const prev = idx >= 0 && idx + 1 < all.length ? all[idx + 1] : null;
      if (!prev) return;
      const prevRows = await DB.ceilings.listForTeam(teamId, prev.id);
      if (!prevRows?.length) return;
      btn.style.display = '';
      btn.textContent = `Copy from ${prev.label || prev.id}`;
      btn.onclick = () => _confirmCopyFromPrev(teamId, prev);
    } catch { /* no prev season - ignore */ }
  }

  function _confirmCopyFromPrev(teamId, prevSeason) {
    UI.modal({
      title: `Copy ceilings from ${prevSeason.label || prevSeason.id}?`,
      body: 'This replaces any ceilings currently set on this team for the active season. You can still adjust them afterwards.',
      buttons: [
        { label: 'Cancel' },
        {
          label: 'Copy',
          style: 'primary',
          action: async () => {
            try {
              await DB.ceilings.copySeason(teamId, prevSeason.id, CONFIG.season.current);
              invalidateCache(teamId, CONFIG.season.current);
              UI.toast('Ceilings copied', 'success');
              showTeam(teamId);
            } catch (err) {
              console.error('Copy ceilings failed:', err);
              UI.toast('Copy failed', 'error');
            }
          },
        },
      ],
    });
  }

  function _renderTeam(rows) {
    const root = document.getElementById('calibrate-team-content');
    if (!root) return;
    const esc = UTILS.escapeHtml;
    const domains = getDomains();

    let html = `
      <div class="calibrate-hint">
        Set the highest level a player on this team can realistically achieve in each pillar.
        Coaches will be unable to rate above these ceilings. Your own ratings are never capped.
        Tap "+ Per-skill overrides" if you need a different ceiling for individual skills.
      </div>
    `;

    domains.forEach(domain => {
      // Distinguish "row exists" from "effective ceiling" so the visual
      // state of the buttons is unambiguous: number buttons highlight only
      // when a DB row exists for the pillar; ∞ highlights only when no row.
      const pillarRow = rows.find(r => r.domain === domain && !r.skill);
      const pillarVal = pillarRow ? pillarRow.max_level : 5;
      const skillRows = rows.filter(r => r.domain === domain && r.skill);
      const expanded  = _expanded.has(domain);
      const overrideCount = skillRows.length;

      html += `
        <div class="calibrate-pillar-card" data-domain="${esc(domain)}">
          <div class="calibrate-pillar-card__row">
            <div class="calibrate-pillar-card__name">${esc(domain)}</div>
            <div class="calibrate-pillar-card__levels">
              ${[1,2,3,4,5].map(n => `
                <button class="calibrate-level-btn ${pillarRow && pillarVal === n ? 'calibrate-level-btn--active' : ''}"
                        data-pillar-level="${n}" data-domain="${esc(domain)}">${n}</button>
              `).join('')}
              <button class="calibrate-level-btn calibrate-level-btn--clear ${!pillarRow ? 'calibrate-level-btn--active' : ''}"
                      data-pillar-clear="1" data-domain="${esc(domain)}" title="No ceiling">∞</button>
            </div>
          </div>
          <button class="calibrate-expand" data-expand="${esc(domain)}">
            ${expanded ? '− Hide' : '+ Per-skill overrides'}${overrideCount ? ` (${overrideCount})` : ''}
          </button>
          ${expanded ? _renderSkillRows(domain, pillarVal, rows) : ''}
        </div>
      `;
    });

    root.innerHTML = html;
    _bindTeamEvents(root, rows);
  }

  function _renderSkillRows(domain, pillarVal, rows) {
    const esc = UTILS.escapeHtml;
    const skills = getSkills(domain);
    if (!skills.length) return '';
    return `
      <div class="calibrate-skill-list">
        ${skills.map(skill => {
          const row = rows.find(r => r.domain === domain && r.skill === skill);
          const effective = row ? row.max_level : pillarVal;
          const isOverride = !!row;
          return `
            <div class="calibrate-skill-row">
              <div class="calibrate-skill-row__name">
                ${esc(skill)}
                ${isOverride
                  ? `<span class="calibrate-skill-row__tag">override</span>`
                  : `<span class="calibrate-skill-row__tag calibrate-skill-row__tag--muted">inherits pillar (${pillarVal})</span>`}
              </div>
              <div class="calibrate-skill-row__levels">
                ${[1,2,3,4,5].map(n => `
                  <button class="calibrate-level-btn calibrate-level-btn--sm ${effective === n ? 'calibrate-level-btn--active' : ''}"
                          data-skill-level="${n}"
                          data-domain="${esc(domain)}"
                          data-skill="${esc(skill)}">${n}</button>
                `).join('')}
                ${isOverride ? `
                  <button class="calibrate-level-btn calibrate-level-btn--sm calibrate-level-btn--clear"
                          data-skill-clear="1"
                          data-domain="${esc(domain)}"
                          data-skill="${esc(skill)}" title="Inherit pillar value">↺</button>
                ` : ''}
              </div>
            </div>
          `;
        }).join('')}
      </div>
    `;
  }

  function _bindTeamEvents(root, rows) {
    // Pillar level buttons (numbers 1-5 - always insert a row, even for 5).
    root.querySelectorAll('[data-pillar-level]').forEach(btn => {
      btn.addEventListener('click', () => {
        const domain = btn.dataset.domain;
        const level  = parseInt(btn.dataset.pillarLevel, 10);
        _setPillarCeiling(domain, level);
      });
    });

    // Pillar "∞" clear - removes the row entirely (truly unset).
    root.querySelectorAll('[data-pillar-clear]').forEach(btn => {
      btn.addEventListener('click', () => {
        _clearPillarCeiling(btn.dataset.domain);
      });
    });

    // Skill level buttons
    root.querySelectorAll('[data-skill-level]').forEach(btn => {
      btn.addEventListener('click', () => {
        const domain = btn.dataset.domain;
        const skill  = btn.dataset.skill;
        const level  = parseInt(btn.dataset.skillLevel, 10);
        _setSkillCeiling(domain, skill, level);
      });
    });

    // Skill clear (revert to pillar inheritance)
    root.querySelectorAll('[data-skill-clear]').forEach(btn => {
      btn.addEventListener('click', () => {
        const domain = btn.dataset.domain;
        const skill  = btn.dataset.skill;
        _clearSkillCeiling(domain, skill);
      });
    });

    // Expand/collapse per-skill overrides
    root.querySelectorAll('[data-expand]').forEach(btn => {
      btn.addEventListener('click', () => {
        const d = btn.dataset.expand;
        if (_expanded.has(d)) _expanded.delete(d);
        else                  _expanded.add(d);
        _renderTeam(_currentRows());
      });
    });
  }

  function _currentRows() {
    const key = `${_currentTeam.id}__${CONFIG.season.current}`;
    return _rowsCache.get(key) || [];
  }

  async function _setPillarCeiling(domain, level) {
    const seasonId = CONFIG.season.current;
    const teamId   = _currentTeam.id;
    try {
      // Always write the row, even at level=5 - the explicit 5 reads as
      // "calibrated" in the UI rather than "untouched".
      await DB.ceilings.set(teamId, seasonId, domain, null, level, AUTH.getCoachName());
      invalidateCache(teamId, seasonId);
      const fresh = await DB.ceilings.listForTeam(teamId, seasonId);
      _rowsCache.set(`${teamId}__${seasonId}`, fresh || []);
      _renderTeam(fresh || []);
      UI.toast('Saved', 'success', 800);
    } catch (err) {
      console.error('Set pillar ceiling failed:', err);
      UI.toast('Save failed', 'error');
    }
  }

  async function _clearPillarCeiling(domain) {
    const seasonId = CONFIG.season.current;
    const teamId   = _currentTeam.id;
    try {
      await DB.ceilings.clear(teamId, seasonId, domain, null);
      invalidateCache(teamId, seasonId);
      const fresh = await DB.ceilings.listForTeam(teamId, seasonId);
      _rowsCache.set(`${teamId}__${seasonId}`, fresh || []);
      _renderTeam(fresh || []);
      UI.toast('Ceiling cleared', 'success', 800);
    } catch (err) {
      console.error('Clear pillar ceiling failed:', err);
      UI.toast('Save failed', 'error');
    }
  }

  async function _setSkillCeiling(domain, skill, level) {
    const seasonId = CONFIG.season.current;
    const teamId   = _currentTeam.id;
    try {
      await DB.ceilings.set(teamId, seasonId, domain, skill, level, AUTH.getCoachName());
      invalidateCache(teamId, seasonId);
      const fresh = await DB.ceilings.listForTeam(teamId, seasonId);
      _rowsCache.set(`${teamId}__${seasonId}`, fresh || []);
      _renderTeam(fresh || []);
      UI.toast('Saved', 'success', 800);
    } catch (err) {
      console.error('Set skill ceiling failed:', err);
      UI.toast('Save failed', 'error');
    }
  }

  async function _clearSkillCeiling(domain, skill) {
    const seasonId = CONFIG.season.current;
    const teamId   = _currentTeam.id;
    try {
      await DB.ceilings.clear(teamId, seasonId, domain, skill);
      invalidateCache(teamId, seasonId);
      const fresh = await DB.ceilings.listForTeam(teamId, seasonId);
      _rowsCache.set(`${teamId}__${seasonId}`, fresh || []);
      _renderTeam(fresh || []);
      UI.toast('Reverted to pillar', 'success', 800);
    } catch (err) {
      console.error('Clear skill ceiling failed:', err);
      UI.toast('Save failed', 'error');
    }
  }

  return {
    showList,
    showTeam,             // secondary entry from team-list
    getEffectiveCeiling,  // evaluation.js reads this to disable level buttons
    fetchCeilingsForTeam,
  };

})();
